import React, { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, extractErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Check, Sparkles, CreditCard, Mail, Shield } from "lucide-react";

const isOwnerRole = (role) => role === "owner" || role === "admin";

export default function Billing() {
  const { company, user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [goingToCheckout, setGoingToCheckout] = useState(null);
  const isOwner = isOwnerRole(user?.role) || user?.is_master;

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data.plans || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const checkout = async (key) => {
    if (!isOwner) { toast.error("Only the workspace owner can change subscriptions"); return; }
    setGoingToCheckout(key);
    try {
      const origin = window.location.origin;
      const { data } = await api.post("/billing/checkout", { plan: key, origin_url: origin });
      if (data.url) window.location.href = data.url;
    } catch (e) { toast.error(extractErr(e)); setGoingToCheckout(null); }
  };

  return (
    <AppShell title="Billing" subtitle="Subscription plans — upgrade anytime">
      <div className="rounded-2xl border border-border bg-card p-5 mb-4 flex flex-wrap items-center gap-3 justify-between">
        <div>
          <div className="text-xs text-muted-foreground">Current plan</div>
          <div className="font-heading text-xl font-semibold capitalize">{(company?.plan || "free_trial").replace("_", " ")} <span className="inline-block ml-2 rounded-full bg-emerald-500/10 text-emerald-500 text-xs px-2 py-0.5 font-normal capitalize">{company?.subscription_status}</span></div>
          {company?.current_period_end && <div className="text-xs text-muted-foreground mt-1">Renews {new Date(company.current_period_end).toLocaleDateString()}</div>}
          {company?.trial_ends_at && (!company?.plan || company.plan === "free_trial") && <div className="text-xs text-muted-foreground mt-1">Trial ends {new Date(company.trial_ends_at).toLocaleDateString()}</div>}
        </div>
      </div>

      {!isOwner && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-300 flex items-center gap-2 mb-4" data-testid="billing-owner-notice">
          <Shield className="w-3.5 h-3.5" />
          <span>Only the workspace owner can subscribe or change plans. Contact your owner to upgrade.</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {loading ? [...Array(3)].map((_, i) => <div key={i} className="rounded-2xl border border-border bg-card h-[480px] animate-pulse" />) :
          plans.map((p) => {
            const isCurrent = (company?.plan || "") === p.key;
            return (
              <div key={p.key} className={`rounded-2xl border bg-card p-5 shadow-sm flex flex-col ${p.recommended ? "ring-2 ring-primary/30 border-primary/30" : "border-border"}`} data-testid={`billing-plan-card-${p.key}`}>
                {p.recommended && <div className="inline-flex items-center gap-1 self-start rounded-full bg-primary text-primary-foreground text-[10px] px-2 py-0.5 mb-2 font-medium"><Sparkles className="w-3 h-3" /> Recommended</div>}
                <div className="font-heading text-lg font-semibold">{p.name}</div>
                <div className="text-sm text-muted-foreground">{p.tagline}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="text-4xl font-semibold tabular-nums">€{p.price_eur}</span>
                  <span className="text-xs text-muted-foreground">/ month</span>
                </div>
                <ul className="mt-4 space-y-2 flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                {p.limits?.length > 0 && (
                  <ul className="mt-3 space-y-1">
                    {p.limits.map((l, i) => <li key={i} className="text-[11px] text-muted-foreground">· {l}</li>)}
                  </ul>
                )}
                <Button className="mt-5 w-full" disabled={isCurrent || goingToCheckout === p.key || !isOwner} onClick={() => checkout(p.key)} data-testid={`billing-checkout-button-${p.key}`}>
                  {isCurrent ? "Current plan" : !isOwner ? "Owner only" : goingToCheckout === p.key ? "Redirecting…" : <><CreditCard className="w-4 h-4 mr-1.5" />Subscribe</>}
                </Button>
              </div>
            );
          })}

        <div className="rounded-2xl border border-dashed border-border bg-transparent p-5 md:col-span-3 text-center">
          <div className="font-heading font-semibold">Enterprise — custom quote</div>
          <p className="text-sm text-muted-foreground mt-1">White-label, API access, custom onboarding, dedicated support.</p>
          <Button variant="secondary" className="mt-3" onClick={() => (window.location.href = "mailto:info@operationsolution.com?subject=Operon%20Enterprise%20Inquiry")}>
            <Mail className="w-4 h-4 mr-1.5" /> Contact sales
          </Button>
        </div>
      </div>
    </AppShell>
  );
}
